<template>
  <div class="rich-text-editor">
    <QuillEditor
      ref="quillEditor"
      v-model:content="content"
      :options="editorOptions"
      content-type="html"
      @update:content="onContentChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import { QuillEditor } from "@vueup/vue-quill";

export default defineComponent({
  name: "RichTextEditor",
  components: {
    QuillEditor,
  },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Write something...",
    },
    height: {
      type: String,
      default: "300px",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const quillEditor = ref();
    const content = ref(props.modelValue);

    const editorOptions = computed(() => ({
      theme: "snow",
      placeholder: props.placeholder,
      readOnly: props.readonly,
      modules: {
        toolbar: [
          ["bold", "italic", "underline", "strike"],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
      },
    }));

    // Watch for external changes to modelValue
    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue !== content.value) {
          content.value = newValue;
        }
      }
    );

    const onContentChange = (value: string) => {
      emit("update:modelValue", value);
    };

    // Expose methods for parent components
    const getQuill = () => quillEditor.value?.getQuill();
    const setContent = (html: string) => {
      content.value = html;
    };
    const getContent = () => content.value;
    const focus = () => quillEditor.value?.focus();

    return {
      quillEditor,
      content,
      editorOptions,
      onContentChange,
      getQuill,
      setContent,
      getContent,
      focus,
    };
  },
});
</script>

<style>
.rich-text-editor .ql-editor {
  min-height: v-bind(height);
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.rich-text-editor .ql-toolbar {
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-bottom: none;
}

.rich-text-editor .ql-container {
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-top: none;
}
</style>
